import { useEffect, useMemo, useState, useRef } from "react";
import api from "@/lib/api";
import { Bell } from "lucide-react";
import { Link } from "react-router-dom";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const STORAGE_KEY = (uid) => `notifs.seen.${uid || "anon"}`;
const MAX_SEEN = 200; // keep localStorage small

function loadSeen(uid) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(uid));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveSeen(uid, set) {
  try {
    const arr = Array.from(set).slice(-MAX_SEEN);
    localStorage.setItem(STORAGE_KEY(uid), JSON.stringify(arr));
    // Broadcast so sibling instances (mobile + desktop bells) stay in sync
    window.dispatchEvent(
      new CustomEvent("notifs:seen-updated", { detail: { uid, seen: arr } }),
    );
  } catch {}
}

export default function NotificationsBell({ user }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [seen, setSeen] = useState(() => loadSeen(user?.id));
  const ref = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/matches");
        if (!alive) return;
        const upcoming = data
          .filter((m) => new Date(m.match_date) > new Date() && m.status !== "finished")
          .sort((a, b) => new Date(a.match_date) - new Date(b.match_date))
          .slice(0, 3);
        const notifs = upcoming.map((m) => ({
          id: `next-${m.id}`,
          match_id: m.id,
          title: m.title || "Match ASS les Régionnaires",
          subtitle: `${formatDate(m.match_date)} · ${m.location}`,
          kind: "upcoming",
        }));

        // MOTM for me in last 5 finished
        const finished = data
          .filter((m) => m.status === "finished")
          .sort((a, b) => new Date(b.match_date) - new Date(a.match_date))
          .slice(0, 5);
        for (const m of finished) {
          if (m.man_of_the_match === user?.id) {
            notifs.push({
              id: `motm-${m.id}`,
              match_id: m.id,
              title: "🏆 Homme du match !",
              subtitle: `${m.title || "Match"} · ${formatDate(m.match_date)}`,
              kind: "motm",
            });
          }
        }
        setItems(notifs);
      } catch (e) {}
    })();
    return () => {
      alive = false;
    };
  }, [user?.id]);

  // Reload seen set when user changes (e.g. login/logout)
  useEffect(() => {
    setSeen(loadSeen(user?.id));
  }, [user?.id]);

  // Keep multiple bell instances (mobile + desktop) in sync
  useEffect(() => {
    const onSeen = (e) => {
      if (!e?.detail) return;
      if (e.detail.uid !== (user?.id || "anon")) return;
      setSeen(new Set(e.detail.seen || []));
    };
    window.addEventListener("notifs:seen-updated", onSeen);
    return () => window.removeEventListener("notifs:seen-updated", onSeen);
  }, [user?.id]);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const unreadCount = useMemo(
    () => items.filter((n) => !seen.has(n.id)).length,
    [items, seen],
  );

  const markAllSeen = () => {
    if (!items.length) return;
    setSeen((prev) => {
      const next = new Set(prev);
      items.forEach((n) => next.add(n.id));
      saveSeen(user?.id, next);
      return next;
    });
  };

  const toggleOpen = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    // Opening the panel clears the badge (all visible notifs marked as seen)
    if (nextOpen) markAllSeen();
  };

  const markOneSeen = (id) => {
    setSeen((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveSeen(user?.id, next);
      return next;
    });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notifications-bell"
        onClick={toggleOpen}
        className="relative p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span
            data-testid="notifications-count"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center"
          >
            {unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-xl bg-slate-900 border border-white/10 shadow-2xl overflow-hidden z-50"
        >
          <div className="p-3 border-b border-white/10 font-display font-bold text-sm uppercase">
            Notifications
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-white/5">
            {items.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">
                Rien de neuf pour l'instant
              </div>
            ) : (
              items.map((n) => {
                const isSeen = seen.has(n.id);
                return (
                  <Link
                    key={n.id}
                    to={`/matches/${n.match_id}`}
                    onClick={() => {
                      markOneSeen(n.id);
                      setOpen(false);
                    }}
                    data-testid={`notif-${n.id}`}
                    className={`block p-3 hover:bg-slate-800 transition-opacity ${
                      isSeen ? "opacity-50" : ""
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {!isSeen && (
                        <span
                          className="shrink-0 w-2 h-2 rounded-full bg-[#E5B85C]"
                          aria-hidden="true"
                        />
                      )}
                      <div className="font-bold text-sm">{n.title}</div>
                    </div>
                    <div className="text-xs text-slate-400 font-mono-stat mt-1">
                      {n.subtitle}
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
